import React from 'react';
import PropTypes from 'prop-types';
import { useRoutes, Navigate } from 'react-router-dom';
import ROUTE_PATH from '../config/routes.config';
import LoginView from '../views/auth/login.view';
import DashboardView from '../views/dashboard/dashboard.view';
import RealtimeDashboardView from '../views/dashboard/realtime-dashboard.view';
import ContentChannelsView from '../views/content/channels.view';
import ContentVideosView from '../views/content/videos.view';
import ContentPlaylistsView from '../views/content/playlists.view';
import SubscribersView from '../views/subscribers/subscribers.view';
import SMTPSettingsView from '../views/smtp/smtp-settings.view';
import AdminUsersView from '../views/admin-users/admin-users.view';
import CouponCodesView from '../views/coupons/coupon-codes.view';
import BillingSubscriptionsView from '../views/billing/subscriptions.view';
import ReportsOverviewView from '../views/reports/overview.view';
import ReportsLiveUsersView from '../views/reports/live-users.view';
import ReportsSessionsView from '../views/reports/sessions.view';
import ReportsAppSessionsView from '../views/reports/app-sessions.view';
import ReportsActiveSubscribers from '../views/reports/active-subscribers.view';
import ReportsMigrationStatus from '../views/reports/migration-status.view';
import ReportsMigrationUsers from '../views/reports/migration-users.view';
import ReportsTrialSubscribers from '../views/reports/trial-subscribers.view';
import ReportsUsersCountOverview from '../views/reports/users-count-overview.view';
import PageNotFoundView from '../views/errors/not-found.view';
import LogoutView from '../views/auth/logout.view';
import ContentAppsView from '../views/content/apps.view';
import AppDefaultContentView from '../views/content/app-default-content.view';
import AppEditView from '../views/content/app-edit.view';
import TagsLibraryView from '../views/advertisers/tag-library.view';
import AssignTagsView from '../views/advertisers/assign-tags.view';
import DeployedTagsView from '../views/advertisers/deployed-tags.view';
import GenresView from '../views/content/genres.view';
import CollectionsView from '../views/content/collections.view';
import PlaylistsView from '../views/content/content-playlists.view';
import VideosView from '../views/content/content-videos.view';
import VideoEditView from '../views/content/content-video-edit.view';
import SeriesView from '../views/content/series.view';
import ImportView from '../views/import-export/import.view';
import ChannelGenresView from '../views/content/channel-genres.view';
import ManageDescriptionsView from '../views/content/manage-descriptions.view';
import ContentOwnersView from '../views/content-owner/content-owner.view';
import AutoLogin from '../views/auth/AutoLogin';
import WorkspaceView from '../views/workspace/workspace.view';
import useAccess from '../hooks/access.hook';
import VideoViewDetails from '../views/content/content-video-details.view';
import ChannelAssignmentView from '../views/content/channel-assignment.view';
import LicenseManagementView from '../views/content/license-management.view';
import AppViewDetails from '../views/content/app-details.view';
import ManageRules from '../views/rulesets/manage-rules.view';
import ProfileView from '../views/auth/profile.view';
import VideoPreviewView from '../views/content/content-video-preview.view';
import ChannelContentSchedulingView from '../views/rulesets/channel-content-scheduling.view';
import ChannelPreviewView from '../views/content/channel-preview.view';
import FailedSubscriptionsView from '../views/reports/failed-subscriptions.view';
import CancelSubscriptionRequestsView from '../views/reports/cancel-subscription-requests.view';
import EmailTemplatesView from '../views/smtp/email-templates.view';
import CouponCodeCategoriesView from '../views/coupons/coupon-code-categories.view';
import BlacklistSources from '../views/blacklist-sources/blacklist-sources.view';
import SystemHealthView from '../views/system-health/system-health.view';
import HLSTranscoderView from '../views/content/hls-transcoder.view';
import ContentSourcesView from '../views/content/content-sources.view';
import SubscriberPromocodeView from '../views/coupons/subscriber-coupon.view';
import ChannelPackager from '../views/content/channel-packager';
import ContentPlaylistAddEditView from '../views/content/content-playlist-add-edit.view';
import ContentOwnerAddEditView from '../views/content-owner/content-owner-add-edit.view';
import ContentOwnerViewDetails from '../views/content-owner/content-owner-view-details.view';
import HLSTranscoderObjectView from '../views/content/hls-transcoder-object.view';
import WebSubscribersView from '../views/reports/channel-web-subscribers.view';
import OzyPlaySettingsView from '../views/advertisers/ozy-play-settings.view';

const AppRouter = ({ isLoggedIn }) => {
  const {
    hasAccess,
    hasOperationAccess,
    hasAdminAccess,
    hasSuperAdminAccess,
    hasContentOwnerAccess,
  } = useAccess();
  const routeMappings = [];

  if (isLoggedIn) {
    routeMappings.push({
      path: ROUTE_PATH.LOGIN,
      element: <Navigate to={ROUTE_PATH.DASHBOARD} />,
      exact: true,
    });

    routeMappings.push({
      path: ROUTE_PATH.DASHBOARD,
      element: <DashboardView />,
      exact: true,
    });

    if (hasSuperAdminAccess()) {
      routeMappings.push({
        path: ROUTE_PATH.ADMIN_USERS,
        element: <AdminUsersView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.AUTOLOGIN,
        element: <AutoLogin />,
        exact: true,
      });
    }

    routeMappings.push({
      path: ROUTE_PATH.LOGOUT,
      element: <LogoutView />,
      exact: true,
    });

    routeMappings.push({
      path: ROUTE_PATH.USER_PROFILE,
      element: <ProfileView />,
      exact: true,
    });

    if (hasAccess('video_library')) {
      routeMappings.push({
        path: ROUTE_PATH.VIDEOS,
        element: <VideosView />,
        exact: true,
      });

      if (hasOperationAccess('video_library', 'edit')) {
        routeMappings.push({
          path: ROUTE_PATH.VIDEO_EDIT,
          element: <VideoEditView />,
          exact: true,
        });
      }

      routeMappings.push({
        path: ROUTE_PATH.VIDEO_VIEW_DETAILS,
        element: <VideoViewDetails />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.VIDEO_PREVIEW,
        element: <VideoPreviewView />,
        exact: true,
      });
    }

    if (hasAdminAccess()) {
      routeMappings.push({
        path: ROUTE_PATH.HLS_TRANSCODER_LEGACY,
        element: <HLSTranscoderView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.HLS_TRANSCODER_OBJECT,
        element: <HLSTranscoderObjectView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.CONTENT_SOURCES,
        element: <ContentSourcesView />,
        exact: true,
      });
    }

    if (hasAccess('series_library')) {
      routeMappings.push({
        path: ROUTE_PATH.SERIES,
        element: <SeriesView />,
        exact: true,
      });
    }

    if (hasAccess('manage_descriptions')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_MANAGE_DESCRIPTIONS,
        element: <ManageDescriptionsView />,
        exact: true,
      });
    }

    if (hasAccess('license_management')) {
      routeMappings.push({
        path: ROUTE_PATH.LICENSE_MANAGEMENT,
        element: <LicenseManagementView />,
        exact: true,
      });
    }

    if (hasAccess('genres')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_GENRES,
        element: <GenresView />,
        exact: true,
      });
    }

    if (hasAccess('playlists')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_ALL_PLAYLISTS,
        element: <PlaylistsView />,
        exact: true,
      });

      if (hasOperationAccess('playlists', 'create')) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_ALL_PLAYLISTS_CREATE,
          element: <ContentPlaylistAddEditView editMode={false} />,
          exact: true,
        });
      }

      if (hasOperationAccess('playlists', 'edit')) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_ALL_PLAYLISTS_EDIT,
          element: <ContentPlaylistAddEditView editMode />,
          exact: true,
        });
      }
    }

    if (hasAccess('collections')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_COLLECTIONS,
        element: <CollectionsView />,
        exact: true,
      });
    }

    if (hasAccess('channel_settings')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_ALL_CHANNELS,
        element: <ContentAppsView />,
        exact: true,
      });

      if (hasOperationAccess('channel_settings', 'edit')) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_CHANNEL_EDIT,
          element: <AppEditView />,
          exact: true,
        });
      }

      routeMappings.push({
        path: ROUTE_PATH.CHANNEL_VIEW_DETAILS,
        element: <AppViewDetails />,
        exact: true,
      });
    }

    if (hasAccess('rule_based_scheduling')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_SCHEDULING,
        element: <ChannelContentSchedulingView />,
        exact: true,
      });
    }

    if (hasAccess('preview_channels')) {
      routeMappings.push({
        path: ROUTE_PATH.CHANNEL_PREVIEW,
        element: <ChannelPreviewView />,
        exact: true,
      });
    }

    if (hasAccess('manual_assignment')) {
      routeMappings.push({
        path: ROUTE_PATH.MANAGE_SCHEDULE,
        element: (
          <ChannelAssignmentView
            hasContentOwnerAccess={hasContentOwnerAccess()}
          />
        ),
        exact: true,
      });
    }

    if (hasAccess('promo_codes')) {
      routeMappings.push({
        path: ROUTE_PATH.COUPONS_CONFIG,
        element: <CouponCodesView />,
        exact: true,
      });
    }

    if (hasAccess('global_assets')) {
      routeMappings.push({
        path: ROUTE_PATH.CHANNEL_DEFAULT_CONTENT,
        element: <AppDefaultContentView />,
        exact: true,
      });
    }

    if (hasAccess('rules_library')) {
      routeMappings.push({
        path: ROUTE_PATH.MANAGE_RULES,
        element: <ManageRules />,
        exact: true,
      });
    }

    if (hasAccess('all_users')) {
      routeMappings.push({
        path: ROUTE_PATH.SUBSCRIBERS,
        element: <SubscribersView />,
        exact: true,
      });
    }

    if (hasAccess('all_subscribers')) {
      routeMappings.push({
        path: ROUTE_PATH.BILLING_SUBSCRIPTIONS,
        element: <BillingSubscriptionsView />,
        exact: true,
      });
    }

    if (hasAccess('distributors')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_OWNERS,
        element: <ContentOwnersView />,
        exact: true,
      });

      if (hasOperationAccess('distributors', 'create')) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_OWNERS_CREATE,
          element: <ContentOwnerAddEditView editMode={false} />,
          exact: true,
        });
      }

      if (hasOperationAccess('distributors', 'edit')) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_OWNERS_EDIT,
          element: <ContentOwnerAddEditView editMode />,
          exact: true,
        });
      }

      routeMappings.push({
        path: ROUTE_PATH.CONTENT_OWNERS_VIEW_DETAILS,
        element: <ContentOwnerViewDetails />,
        exact: true,
      });
    }

    if (hasAccess('advertising')) {
      routeMappings.push({
        path: ROUTE_PATH.ADVERTISERS_TAG_LIBRARY,
        element: <TagsLibraryView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.ADVERTISERS_TAG_ASSIGN,
        element: <AssignTagsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.OZYPLAY_SETTINGS,
        element: <OzyPlaySettingsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.ADVERTISERS_TAG_DEPLOYED,
        element: <DeployedTagsView />,
        exact: true,
      });
    }

    if (hasAccess('analytics')) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_OVERVIEW,
        element: <ReportsOverviewView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REALTIME_DASHBOARD,
        element: <RealtimeDashboardView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_LIVE_USERS,
        element: <ReportsLiveUsersView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_ACTIVE_SUBSCRIBERS,
        element: <ReportsActiveSubscribers />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_TRIAL_SUBSCRIBERS,
        element: <ReportsTrialSubscribers />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_CHANNEL_ALL_USERS,
        element: <ReportsUsersCountOverview />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.CONTENT_CHANNELS,
        element: <ContentChannelsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.CONTENT_VIDEOS,
        element: <ContentVideosView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.CONTENT_PLAYLISTS,
        element: <ContentPlaylistsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_SESSIONS,
        element: <ReportsSessionsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_APP_SESSIONS,
        element: <ReportsAppSessionsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_MIGRATION_STATUS,
        element: <ReportsMigrationStatus />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.REPORTS_MIGRATION_USERS,
        element: <ReportsMigrationUsers />,
        exact: true,
      });

      if (hasSuperAdminAccess()) {
        routeMappings.push({
          path: ROUTE_PATH.FAILED_SUBSCRIPTIONS,
          element: <FailedSubscriptionsView />,
          exact: true,
        });

        routeMappings.push({
          path: ROUTE_PATH.CANCEL_SUBSCRIPTION_REQUESTS,
          element: <CancelSubscriptionRequestsView />,
          exact: true,
        });

        routeMappings.push({
          path: ROUTE_PATH.REPORTS_PROMO_CODE,
          element: <SubscriberPromocodeView />,
          exact: true,
        });
      }
    }

    if (hasAccess('import') || hasAccess('export')) {
      routeMappings.push({
        path: ROUTE_PATH.IMPORT,
        element: <ImportView />,
        exact: true,
      });
    }

    if (hasAccess('workspaces')) {
      routeMappings.push({
        path: ROUTE_PATH.WORKSPACE_SETTINGS,
        element: <WorkspaceView />,
        exact: true,
      });
    }

    if (hasAccess('mail_server')) {
      routeMappings.push({
        path: ROUTE_PATH.SMTP_SETTINGS,
        element: <SMTPSettingsView />,
        exact: true,
      });
    }

    if (hasAccess('email_templates')) {
      routeMappings.push({
        path: ROUTE_PATH.EMAIL_TEMPLATES,
        element: <EmailTemplatesView />,
        exact: true,
      });
    }

    if (hasAccess('channel_genres')) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_CHANNEL_GENRES,
        element: <ChannelGenresView />,
        exact: true,
      });
    }

    if (hasAccess('promo_code_categories')) {
      routeMappings.push({
        path: ROUTE_PATH.COUPONS_CATEGORIES_SETTINGS,
        element: <CouponCodeCategoriesView />,
        exact: true,
      });
    }

    if (hasAccess('blacklist_sources')) {
      routeMappings.push({
        path: ROUTE_PATH.BLACKLIST_SOURCES_SETTINGS,
        element: <BlacklistSources />,
        exact: true,
      });
    }

    if (hasAccess('system_health')) {
      routeMappings.push({
        path: ROUTE_PATH.SYSTEM_HEALTH_SETTINGS,
        element: <SystemHealthView />,
        exact: true,
      });
    }
    // TODO - below routes to be added based on access rights once implemented
    if (hasAccess('channel_packager')) {
      routeMappings.push({
        path: ROUTE_PATH.CHANNEL_PACKAGER,
        element: <ChannelPackager />,
        exact: true,
      });
    }

    if (hasAccess('analytics')) {
      routeMappings.push({
        path: ROUTE_PATH.WEBSUBSCRIBER_ANALYTICS,
        element: <WebSubscribersView />,
        exact: true,
      });
    }
  }

  routeMappings.push({
    path: '*',
    element: isLoggedIn ? <PageNotFoundView /> : <LoginView />,
  });

  const routes = useRoutes(routeMappings);

  return routes;
};

AppRouter.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
};

export default AppRouter;
